import { isReactNativeWebView, hasReactNativeMessageChannel } from 'src/utils/webview-detection';
import { AuthUtils } from './Auth.utils';
import { AuthJwtObject } from './Auth.types';
import {
  planAuthReplyNavigation,
  isTrustedReplySource,
  MAX_AUTH_REPLY_NAVIGATIONS,
} from './webview-auth-reply.logic';
import { shouldAdoptToken, isTokenUsable } from './auth-token-adoption.logic';
import { createBoundedSessionCounter } from './bounded-counter.logic';
import { STORAGE_KEYS } from 'src/services/StorageService/StorageService';
import { Logger } from 'src/utils/logger';

/**
 * Service to handle authentication in a React Native WebView context
 */
export class WebViewAuthService {
  /**
   * Checks if we're running in a WebView and can receive auth tokens
   */
  static isWebViewAuthEnabled(): boolean {
    return isReactNativeWebView() && hasReactNativeMessageChannel();
  }

  /**
   * Custom event name for secure WebView authentication
   * This specific name creates a dedicated channel for auth messages only
   */
  private static readonly AUTH_EVENT_NAME = 'BECREATIVES_AUTH_EVENT';

  /**
   * How many AUTH_TOKEN replies have navigated in this browsing context.
   *
   * Durable (sessionStorage) because `window.location.replace` starts a new document, and a
   * reply to a superseded request lands in THAT document — a module flag would reset exactly
   * when the bound is needed. Preserved across logout cleanup
   * (SESSION_STORAGE_PRESERVED_KEYS): a wiped bound is no bound.
   *
   * NEVER handed back. There is no safe moment to: this budget's hazard is a superseded reply
   * still in flight, and every moment inside a freshly loaded document — settle, a successful
   * refresh, a new session — is inside that window, so any reset lets the reply that caused
   * the load clear the bound one hop later. Not even the session epoch that hands the RECOVERY
   * budget back qualifies: native writes CF's JWT key directly (mobile-app
   * useWebviewAuth.ts:219), which raises a cross-document `storage` event that useAuth turns
   * into a `startSession()`, so on the failure path the epoch can bump once per document too.
   *
   * Exhausting it is a SOFT failure, and it is worth being exact about why, because the screen
   * that survives it (WebviewSessionExpired) offers Retry as its only affordance and that must
   * not be theatre. Nothing about clearing the gate runs through this navigation:
   * `listenForAuthMessages` calls back BEFORE consulting the budget, so a reply still adopts an
   * upgraded token (`handleWebViewToken`) and still flips `isWebViewReady`, which re-enables
   * the brands query and takes the terminal screen down. `Http.requestTokenFromMobile` also
   * picks the token up by polling storage, and a logged-in user is routed client-side
   * (protected-route.tsx). What IS lost, permanently, for the life of the WebView: honouring
   * the `route` native asked us to open on a reply. A cosmetic landing-screen difference, not
   * the session and not the render — and if native answers with nothing usable, the retry
   * screen's own RETRY_FEEDBACK_MS timer gives the user the honest "close and reopen" verdict
   * rather than leaving them waiting.
   *
   * The in-memory floor in createBoundedSessionCounter does NOT help here, unlike for the
   * recovery counter: `bump()` is immediately followed by `location.replace`, which destroys
   * module state. So on a device where DOM storage is unavailable this bound does not hold at
   * all. Saying so plainly rather than implying a protection that isn't there — the honest
   * statement is that the navigation bound depends on sessionStorage, and the recovery bound
   * (which is re-read within one document) does not.
   */
  private static readonly navigations = createBoundedSessionCounter(
    STORAGE_KEYS.SESSION.WV_AUTH_REPLY_NAVIGATIONS,
    MAX_AUTH_REPLY_NAVIGATIONS,
  );

  /**
   * Whether a `message` event came from this document itself, i.e. from native's injected
   * script rather than from an embedded frame. See isTrustedReplySource for the reasoning and
   * for why it fails soft.
   */
  private static isSelfPostedMessage(event: MessageEvent): boolean {
    return isTrustedReplySource({
      sourceIsSelf: event.source == null ? null : event.source === window,
      origin: event.origin ?? '',
      ownOrigin: window.location.origin,
    });
  }

  /**
   * Decode a reply token into `{ token, expires }`, treating an undecodable one as UNDATABLE
   * rather than as a thrown error.
   *
   * Native does not promise a decodable JWT — the string can be truncated, opaque, or a
   * placeholder — and `jwt_decode` throws on those. Letting the throw escape defeats the
   * "ALWAYS call back" guarantee the handler is built around: the callback is what flips
   * `isWebViewReady`, so a single malformed reply left the gate on the `!isWebViewReady` arm
   * for the whole recovery budget and then rendered the terminal screen. That is the blank
   * screen this branch exists to remove, reachable from one bad string.
   *
   * NaN is the honest expiry for "cannot be dated", and the downstream decisions already
   * handle it: isTokenUsable requires a finite expiry and shouldAdoptToken refuses an
   * unusable token, both asserted in auth-token-adoption.logic.test.ts. So this token is
   * reported, not adopted, not navigated on — and the handshake still completes.
   */
  private static decodeReplyToken(token: string): AuthJwtObject {
    try {
      return AuthUtils.make(token);
    } catch {
      Logger.error(
        'AUTH_TOKEN reply carried an undecodable token; treating it as unusable',
        'WebViewAuth',
      );
      return { token, expires: NaN };
    }
  }

  /**
   * Listens for authentication messages from the React Native app
   * @param callback Function to call when auth data is received
   */
  static listenForAuthMessages(
    callback: (authObj: AuthJwtObject, adopted: boolean) => void,
  ): () => void {
    if (!this.isWebViewAuthEnabled()) {
      return () => {}; // Return no-op cleanup if not in WebView
    }

    // Handler for receiving secure auth events
    const handleAuthEvent = (event: CustomEvent) => {
      try {
        const data = event.detail;

        // Validate the auth data format and required properties
        if (
          data &&
          typeof data === 'object' &&
          'token' in data &&
          typeof data.token === 'string' &&
          data.token.length > 0
        ) {
          // Decode ONCE, here. The old flow called back first (which decoded inside
          // setWebViewAuth) and then decoded again to read the expiry — two parses of the
          // same string on the auth boot path, and two places jwt_decode could throw.
          const incoming = this.decodeReplyToken(data.token);
          const now = Date.now();
          const usable = isTokenUsable(incoming.expires, now);
          const adopt = shouldAdoptToken({
            incomingExpires: incoming.expires,
            // getJwt() never returns null — it answers { token: '', expires: 0 } when nothing
            // valid is stored, which isTokenUsable already rejects.
            currentExpires: AuthUtils.getJwt().expires,
            now,
          });

          // ALWAYS call back, even when refusing the token, and let the callback decide
          // what to do with each half. It also flips isWebViewReady, and withholding that
          // was its own regression: a logged-out CF answered with a dead token would keep
          // the full-page spinner for the whole recovery budget (~13s) before reaching the
          // login form it used to reach immediately.
          //
          // `adopted` is separate because only an UPGRADE may be written: overwriting a JWT
          // CF just rotated with native's older copy (independent stores) logs a paying
          // customer out of a working session, and before this check each such cycle
          // re-armed itself into an unbounded reload loop. See auth-token-adoption.logic.
          callback(incoming, adopt);

          const navigationsUsed = this.navigations.read();
          const { navigate, url, reason } = planAuthReplyNavigation({
            route: data.route,
            currentPath: window.location.pathname,
            search: window.location.search,
            tokenUsable: usable,
            navigationsUsed,
            // Read at reply time, not captured at listener setup: connectivity is exactly the
            // thing that changes between the two. Unknown counts as ONLINE, matching the gate
            // (auth-layout) — the safe direction there, since the alternative suppresses real
            // navigations on any environment without the global.
            isOffline: typeof navigator !== 'undefined' && navigator.onLine === false,
          });

          if (!navigate) {
            // ERROR level, not warn: a suppressed navigation means the user may sit on a
            // screen they did not ask for, and this is the only trace of why. Logger.error is
            // the right call for that — ERROR is precisely the level that still emits in
            // production (utils/logger.ts sets LogLevel.ERROR for PROD), and it carries the
            // prefix, timestamp and origin that make a line findable in a bug report.
            Logger.error(
              `Suppressed AUTH_TOKEN navigation (${reason}); token ${adopt ? 'adopted' : 'refused'}`,
              'WebViewAuth',
            );
            return;
          }

          this.navigations.bump();
          window.location.replace(url);
        } else {
          console.warn('Received invalid auth data format');
        }
      } catch (error) {
        console.error('Error processing WebView auth event:', error);
      }
    };

    // Set up a bridge function to handle regular postMessage from React Native
    // and convert it to our custom event
    const messageEventBridge = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        // Only process auth token messages
        if (data && data.type === 'AUTH_TOKEN' && data.token) {
          // Only this document may hand us a token.
          //
          // Native delivers AUTH_TOKEN by injecting `window.postMessage(json)` into the TOP
          // frame (mobile-app utils/webview.ts:7 via webviewRef.injectJavaScript), so a
          // genuine reply is this document posting to itself: `source === window`, own
          // origin. Anything else is another frame — and this listener is app-wide, so
          // without the check any embedded third-party frame could post
          // `{type:'AUTH_TOKEN', token, route}` and have the token adopted (adoption compares
          // only `exp`, which the sender controls) and `route` fed to location.replace.
          //
          // Checked HERE, after the type test, not at the top of the handler. This listener
          // sees every postMessage on the page — Stripe Elements alone posts on a heartbeat,
          // plus HubSpot, Canny, UserGuiding — so at the top it logged an error per message,
          // saying "AUTH_TOKEN-channel message" about traffic that was nothing of the kind,
          // and buried the one occurrence that would matter. Same security property: nothing
          // is dispatched before the check.
          //
          // Deliberately fails SOFT: it rejects only what it can positively attribute to
          // another frame, and allows anything where `source`/`origin` are unavailable.
          // Getting this wrong in the strict direction would drop every real reply and
          // produce the blank screen this branch exists to remove.
          if (!this.isSelfPostedMessage(event)) {
            Logger.error('Ignoring an AUTH_TOKEN posted by a foreign frame', 'WebViewAuth');
            return;
          }

          // Dispatch our secure custom event
          window.dispatchEvent(
            new CustomEvent(this.AUTH_EVENT_NAME, {
              detail: { token: data.token, route: data.route },
            }),
          );
        }
      } catch (error) {
        // Silently ignore parsing errors - could be unrelated messages
      }
    };

    // Add event listeners
    window.addEventListener(this.AUTH_EVENT_NAME, handleAuthEvent as EventListener);
    window.addEventListener('message', messageEventBridge);

    // Return cleanup function
    return () => {
      window.removeEventListener(this.AUTH_EVENT_NAME, handleAuthEvent as EventListener);
      window.removeEventListener('message', messageEventBridge);
    };
  }

  /**
   * Sends a message to the React Native app to request authentication
   * Uses a secure format with a nonce to prevent replay attacks
   */
  static requestAuthFromNative(): void {
    if (!this.isWebViewAuthEnabled() || !('ReactNativeWebView' in window)) {
      return;
    }

    // Generate a random nonce for this request
    // This helps prevent replay attacks and makes each request unique
    const nonce =
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    // Create a timestamp for the request
    const timestamp = Date.now();

    // We've already checked that ReactNativeWebView exists
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'REQUEST_AUTH_TOKEN',
        secure: true,
        eventName: this.AUTH_EVENT_NAME,
        nonce,
        timestamp,
      }),
    );

    console.log(`Sent secure auth request with event: ${this.AUTH_EVENT_NAME}`);
  }
}
